import axios from 'axios';
import API_CONFIG from '../config/api';

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

export interface UserMessage {
  _id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  priority: 'low' | 'medium' | 'high';
  userId: string;
  category?: string;
  actionUrl?: string;
  expiresAt?: string;
}

export interface MessageFilters {
  isRead?: boolean;
  type?: 'info' | 'warning' | 'error' | 'success';
  priority?: 'low' | 'medium' | 'high';
  category?: string;
  page?: number;
  limit?: number;
}

export interface MessagesResponse {
  success: boolean;
  data: UserMessage[];
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
    limit: number;
  };
  unreadCount: number;
}

/**
 * Get user messages with optional filters
 */
export const getUserMessages = async (filters: MessageFilters = {}): Promise<MessagesResponse> => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('user/messages'),
      {
        params: filters,
        ...getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Mark a message as read
 */
export const markMessageAsRead = async (messageId: string): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.patch(
      API_CONFIG.getUrl(`user/messages/${messageId}/read`),
      {},
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Mark multiple messages as read
 */
export const markMultipleMessagesAsRead = async (messageIds: string[]): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.patch(
      API_CONFIG.getUrl('user/messages/mark-read'),
      { messageIds },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Mark all messages as read
 */
export const markAllMessagesAsRead = async (): Promise<{ success: boolean; message?: string; modifiedCount?: number; unreadCountBefore?: number; unreadCountAfter?: number }> => {
  try {
    const response = await axios.patch(
      API_CONFIG.getUrl('user/messages/mark-all-read'),
      {},
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a message
 */
export const deleteMessage = async (messageId: string): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.delete(
      API_CONFIG.getUrl(`user/messages/${messageId}`),
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Delete multiple messages
 */
export const deleteMultipleMessages = async (messageIds: string[]): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.delete(
      API_CONFIG.getUrl('user/messages'),
      {
        data: { messageIds },
        ...getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get unread messages count
 */
export const getUnreadMessagesCount = async (): Promise<{ count: number }> => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('user/messages/unread-count'),
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get message categories
 */
export const getMessageCategories = async (): Promise<{ categories: string[] }> => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('user/messages/categories'),
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
}; 